import styled from 'styled-components';

export const SearchContainer = styled.div`
  background: var(--bg-section);
  min-height: calc(100vh - 68px);
  padding: 40px 20px 72px;

  > * {
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const SearchHeader = styled.header`
  margin-bottom: 24px;

  .eyebrow {
    display: inline-block;
    background: var(--primary-subtle);
    color: var(--primary);
    font-size: var(--text-xs);
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 4px 12px;
    border-radius: var(--radius-round);
    margin-bottom: 12px;
  }

  h1 {
    font-size: var(--text-3xl);
    letter-spacing: -0.5px;
    margin-bottom: 4px;
  }

  p {
    color: var(--text-secondary);
  }
`;

export const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: var(--bg-card);
  border: 2px solid var(--border-medium);
  border-radius: var(--radius-round);
  padding: 6px 6px 6px 20px;
  box-shadow: var(--shadow-sm);
  margin-bottom: 20px;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);

  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 4px var(--focus-ring);
  }

  @media (max-width: 560px) {
    flex-direction: column;
    border-radius: var(--radius-lg);
    padding: 12px;

    button { width: 100%; }
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: var(--text-base);
  font-family: var(--font-brand);
  padding: 10px 4px;
  min-width: 0;

  &::placeholder { color: var(--text-muted); }
  &:focus { outline: none; }

  @media (max-width: 560px) {
    width: 100%;
    padding: 10px 12px;
    background: var(--bg-input);
    border-radius: var(--radius-md);
  }
`;

export const FiltersSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 18px;
  margin-bottom: 32px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const FilterGroup = styled.div``;

export const FilterLabel = styled.label`
  display: block;
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 6px;
`;

export const FilterSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  background: var(--bg-input);
  border: 1.5px solid transparent;
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-family: var(--font-brand);
  font-size: var(--text-sm);
  cursor: pointer;
  transition: border-color var(--transition-fast);

  &:hover { border-color: var(--border-medium); }

  &:focus {
    outline: none;
    border-color: var(--border-focus);
  }
`;

export const ResultsSection = styled.section``;

export const ResultsCount = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: 600;
  margin-bottom: 18px;
`;

export const ActivityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

export const ActivityCard = styled.article`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  overflow: hidden;
  cursor: pointer;
  box-shadow: var(--shadow-xs);
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-hover);
  }
`;

export const ActivityImageWrap = styled.div`
  position: relative;
  overflow: hidden;
  height: 190px;
`;

export const ActivityImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.5s ease;

  ${ActivityCard}:hover & {
    transform: scale(1.05);
  }
`;

export const ActivityBadge = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  background: var(--primary);
  color: var(--white);
  font-size: var(--text-xs);
  font-weight: 700;
  padding: 4px 10px;
  border-radius: var(--radius-round);
`;

export const ActivityInfo = styled.div`
  padding: 18px;
`;

export const ActivityTitle = styled.h3`
  font-size: var(--text-lg);
  margin-bottom: 6px;
  line-height: 1.3;
`;

export const ActivityDescription = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.55;
  margin-bottom: 10px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ActivityLocation = styled.p`
  color: var(--text-muted);
  font-size: var(--text-xs);
  margin-bottom: 12px;
`;

export const ActivityMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid var(--border-light);

  .duration {
    color: var(--text-muted);
    font-size: var(--text-xs);
  }
`;

export const ActivityPrice = styled.span`
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--secondary);
`;

export const NoResults = styled.div`
  text-align: center;
  padding: 64px 20px;
  background: var(--bg-card);
  border: 1px dashed var(--border-medium);
  border-radius: var(--radius-lg);

  h3 {
    margin-bottom: 8px;
  }

  p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
  }
`;
